// `one` is format 1 (implied consent); `three` is format 3 (explicit consent).
export const US_BANNER_VARIANTS = {
    OPT_OUT: 'one',
    OPT_IN: 'three',
};

export const US_BANNER_MODES = {
    DEFAULT: 'default',
    OPT_OUT_ALL: 'optOutAll',
    OPT_IN_ALL: 'optInAll',
    CUSTOM: 'custom',
};

export const FALLBACK_TO_OSANO = 'fallbackToOsano';

const US = 'us';
const US_STATE_CODE = /^us-[a-z]{2}$/;
const VARIANTS = Object.values(US_BANNER_VARIANTS);

const isVariant = variant => VARIANTS.includes(variant);

const isUsJurisdiction = code => code === US || US_STATE_CODE.test(code);

const jurisdictionEntries = variantMapping =>
    Object.entries(variantMapping?.byJurisdiction || {}).filter(
        ([code, variant]) => isUsJurisdiction(code) && isVariant(variant)
    );

const stateEntries = variantMapping =>
    jurisdictionEntries(variantMapping).filter(([code]) => code !== US);

const countryVariant = variantMapping =>
    jurisdictionEntries(variantMapping).find(([code]) => code === US)?.[1];

export const normalizeUsVariantMapping = variantMapping => {
    const entries = jurisdictionEntries(variantMapping);
    // {} rather than a bare behavior key: an empty mapping is never stored.
    return entries.length
        ? { byJurisdiction: Object.fromEntries(entries), behavior: FALLBACK_TO_OSANO }
        : {};
};

export const getUsBannerOptInStates = variantMapping =>
    stateEntries(variantMapping)
        .filter(([, variant]) => variant === US_BANNER_VARIANTS.OPT_IN)
        .map(([code]) => code);

export const makeUsVariantMapping = (mode, optInStates = []) => {
    switch (mode) {
        case US_BANNER_MODES.OPT_OUT_ALL:
            return {
                byJurisdiction: { [US]: US_BANNER_VARIANTS.OPT_OUT },
                behavior: FALLBACK_TO_OSANO,
            };
        case US_BANNER_MODES.OPT_IN_ALL:
            return {
                byJurisdiction: { [US]: US_BANNER_VARIANTS.OPT_IN },
                behavior: FALLBACK_TO_OSANO,
            };
        case US_BANNER_MODES.CUSTOM: {
            const optIn = (optInStates || []).filter(code => US_STATE_CODE.test(code));
            // An empty selection stays {}; `us` alone would read as Opt-out-All.
            if (!optIn.length) {
                return {};
            }
            // The `us` entry makes Custom exhaustive: unlisted states opt out
            // instead of inheriting the curated format for newly legislated states.
            return {
                byJurisdiction: {
                    ...Object.fromEntries(optIn.map(code => [code, US_BANNER_VARIANTS.OPT_IN])),
                    [US]: US_BANNER_VARIANTS.OPT_OUT,
                },
                behavior: FALLBACK_TO_OSANO,
            };
        }
        default:
            return {};
    }
};

// `usBannerOverride` is form-only: "Default" and "custom, nothing picked" are both `{}`.
export const getUsBannerMode = ({ variantMapping, usBannerOverride } = {}) => {
    if (!usBannerOverride) {
        return US_BANNER_MODES.DEFAULT;
    }
    if (!stateEntries(variantMapping).length) {
        const country = countryVariant(variantMapping);
        if (country === US_BANNER_VARIANTS.OPT_OUT) {
            return US_BANNER_MODES.OPT_OUT_ALL;
        }
        if (country === US_BANNER_VARIANTS.OPT_IN) {
            return US_BANNER_MODES.OPT_IN_ALL;
        }
    }
    return US_BANNER_MODES.CUSTOM;
};

export const hasMissingOptInStates = formValues =>
    getUsBannerMode(formValues) === US_BANNER_MODES.CUSTOM &&
    !stateEntries(formValues?.variantMapping).length;

export const toUsBannerFormValues = ({ variantMapping, ccpaRelaxed } = {}) => {
    const mapping = normalizeUsVariantMapping(variantMapping);
    if (jurisdictionEntries(mapping).length) {
        return { variantMapping: mapping, usBannerOverride: true };
    }
    return ccpaRelaxed
        ? {
              variantMapping: makeUsVariantMapping(US_BANNER_MODES.OPT_OUT_ALL),
              usBannerOverride: true,
          }
        : { variantMapping: {}, usBannerOverride: false };
};

// Mirrors the server's rewrite so anything still reading ccpaRelaxed agrees.
export const toCcpaRelaxed = variantMapping =>
    !stateEntries(variantMapping).length &&
    countryVariant(variantMapping) === US_BANNER_VARIANTS.OPT_OUT;

// `undefined` = not covered; the caller falls through to ccpaRelaxed, then curated.
export const getUsVariantOverride = (variantMapping, jurisdiction) => {
    const code = String(jurisdiction || '').toLowerCase();
    if (!variantMapping || !isUsJurisdiction(code)) {
        return undefined;
    }
    const byJurisdiction = variantMapping.byJurisdiction || {};
    // Same resolution order as the edge.
    const variant = byJurisdiction[code] ?? byJurisdiction[US];
    return isVariant(variant) ? variant : undefined;
};
